import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { STATUS_LABELS, STATUS_COLORS, ROLE_LABELS } from "@/lib/constants";
import Shell from "@/components/Shell";
import { StarDisplay } from "@/components/StarRating";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import {
  Users, Building2, Truck, Wrench, Star, Gauge, ShieldCheck, LayoutDashboard, Check, X, Clock, MapPin, Plus, Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { AdminPayments } from "@/components/AdminPayments";
import { AdminPayouts } from "@/components/AdminPayouts";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { EditProfileDialog } from "@/components/EditProfileDialog";
import { AdminAccountTools } from "@/components/AdminAccountTools";
import { SupportAgents } from "@/components/support/SupportAgents";
import { ContractTemplatesDialog } from "@/components/ContractTemplatesDialog";
import { ProviderContractCell } from "@/components/ProviderContractCell";

const USER_STATUS = {
  active: { label: "معتمد", cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
  pending: { label: "قيد المراجعة", cls: "bg-amber-500/10 text-amber-600 border-amber-500/20" },
  rejected: { label: "مرفوض", cls: "bg-destructive/10 text-destructive border-destructive/20" },
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [tows, setTows] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [cities, setCities] = useState([]);
  const [newCity, setNewCity] = useState("");

  useEffect(() => {
    (async () => {
      const [s, u, r, t, rt, c] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/users"),
        api.get("/admin/requests"), api.get("/admin/tow-requests"), api.get("/admin/ratings"),
        api.get("/admin/cities"),
      ]);
      setStats(s.data); setUsers(u.data); setRequests(r.data); setTows(t.data); setRatings(rt.data); setCities(c.data);
    })();
  }, []);

  if (!stats) return <Shell title="لوحة التحكم"><div /></Shell>;

  const reloadCities = async () => {
    const c = await api.get("/admin/cities");
    setCities(c.data);
  };
  const addCity = async (e) => {
    e.preventDefault();
    const name = newCity.trim();
    if (!name) return;
    try {
      await api.post("/admin/cities", { name });
      toast.success("تمت إضافة المدينة");
      setNewCity(""); reloadCities();
    } catch (err) { toast.error(err.response?.data?.detail || "تعذّر الإضافة"); }
  };
  const delCity = async (id) => {
    try { await api.delete(`/admin/cities/${id}`); toast.success("تم حذف المدينة"); reloadCities(); }
    catch { toast.error("تعذّر الحذف"); }
  };

  const reloadUsers = async () => {
    const u = await api.get("/admin/users");
    setUsers(u.data);
  };
  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/users/${id}/status`, { status });
      toast.success(status === "active" ? "تم اعتماد الحساب" : "تم رفض الحساب");
      reloadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "تعذّر تحديث الحالة"); }
  };
  const pendingCount = users.filter((u) => u.status === "pending" && ["workshop", "tow_provider"].includes(u.role)).length;

  const cards = [
    { label: "العملاء", value: stats.customers, icon: Users, color: "text-primary bg-primary/10" },
    { label: "الورش", value: stats.workshops, icon: Building2, color: "text-emerald-600 bg-emerald-500/10" },
    { label: "السطحات", value: stats.tow_providers, icon: Truck, color: "text-accent bg-accent/10" },
    { label: "طلبات الصيانة", value: stats.requests, icon: Wrench, color: "text-violet-600 bg-violet-500/10" },
    { label: "طلبات السحب", value: stats.tow_requests, icon: Truck, color: "text-accent bg-accent/10" },
    { label: "عروض الأسعار", value: stats.quotes, icon: LayoutDashboard, color: "text-cyan-600 bg-cyan-500/10" },
    { label: "طلبات نشطة", value: stats.open_requests, icon: Gauge, color: "text-amber-600 bg-amber-500/10" },
    { label: "التقييمات", value: stats.ratings, icon: Star, color: "text-yellow-600 bg-yellow-500/10" },
  ];

  const chartData = Object.entries(stats.by_status).map(([k, v]) => ({ name: STATUS_LABELS[k], value: v }));
  const COLORS = ["#2563EB", "#6366F1", "#8B5CF6", "#F59E0B", "#06B6D4", "#F97316", "#10B981", "#22C55E", "#64748B"];

  return (
    <Shell title="لوحة تحكم الإدارة" subtitle="نظرة شاملة على المنصة والمستخدمين والطلبات" icon={ShieldCheck} accent="primary" actions={<div className="flex flex-wrap gap-2"><EditProfileDialog /><ChangePasswordDialog /></div>}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => (
          <Card key={c.label} className="p-5 flex items-center gap-4" data-testid={`stat-${c.label}`}>
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${c.color}`}><c.icon className="w-6 h-6" /></div>
            <div><div className="text-2xl font-extrabold font-heading">{c.value}</div><div className="text-sm text-muted-foreground">{c.label}</div></div>
          </Card>
        ))}
      </div>

      <Card className="p-6 mb-8">
        <h3 className="font-bold mb-4">توزيع طلبات الصيانة حسب الحالة</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData}>
            <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: "IBM Plex Sans Arabic" }} interval={0} angle={-20} textAnchor="end" height={60} />
            <YAxis allowDecimals={false} />
            <Tooltip contentStyle={{ fontFamily: "IBM Plex Sans Arabic", borderRadius: 12 }} />
            <Bar dataKey="value" radius={[8, 8, 0, 0]}>
              {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Tabs defaultValue="users" dir="rtl">
        <TabsList className="h-auto p-1 flex-wrap">
          <TabsTrigger value="users" className="h-auto py-2 gap-2" data-testid="admin-tab-users">
            المستخدمون
            {pendingCount > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center" data-testid="pending-badge">{pendingCount}</span>}
          </TabsTrigger>
          <TabsTrigger value="requests" className="h-auto py-2" data-testid="admin-tab-requests">طلبات الصيانة</TabsTrigger>
          <TabsTrigger value="tows" className="h-auto py-2" data-testid="admin-tab-tows">طلبات السحب</TabsTrigger>
          <TabsTrigger value="payments" className="h-auto py-2" data-testid="admin-tab-payments">المدفوعات</TabsTrigger>
          <TabsTrigger value="ratings" className="h-auto py-2" data-testid="admin-tab-ratings">التقييمات</TabsTrigger>
          <TabsTrigger value="cities" className="h-auto py-2" data-testid="admin-tab-cities">المدن</TabsTrigger>
          <TabsTrigger value="support" className="h-auto py-2" data-testid="admin-tab-support">خدمة العملاء</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-6">
          <AdminAccountTools onChanged={reloadUsers} />
          <div className="flex justify-end mb-3"><ContractTemplatesDialog /></div>
          <Card className="overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-start">الاسم</TableHead>
                <TableHead className="text-start">النوع</TableHead>
                <TableHead className="text-start">المدينة</TableHead>
                <TableHead className="text-start">الجوال</TableHead>
                <TableHead className="text-start">البريد الإلكتروني</TableHead>
                <TableHead className="text-start">التقييم</TableHead>
                <TableHead className="text-start">الحالة</TableHead>
                <TableHead className="text-start">العقد</TableHead>
                <TableHead className="text-start">الإجراءات</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {users.map((u) => {
                  const isProvider = ["workshop", "tow_provider"].includes(u.role);
                  const st = USER_STATUS[u.status] || USER_STATUS.active;
                  return (
                    <TableRow key={u.id} data-testid={`admin-user-${u.id}`}>
                      <TableCell className="font-semibold">{u.name}</TableCell>
                      <TableCell><Badge variant="outline">{ROLE_LABELS[u.role]}</Badge></TableCell>
                      <TableCell>{u.city}</TableCell>
                      <TableCell dir="ltr" className="text-start">{u.phone}</TableCell>
                      <TableCell dir="ltr" className="text-start text-sm">
                        <a href={`mailto:${u.email}`} className="text-primary hover:underline break-all" data-testid={`admin-user-email-${u.id}`}>{u.email}</a>
                      </TableCell>
                      <TableCell>{u.rating_count > 0 ? <div className="flex items-center gap-1"><StarDisplay value={u.rating_avg} size="w-3.5 h-3.5" /><span className="text-xs">({u.rating_count})</span></div> : "—"}</TableCell>
                      <TableCell>{isProvider ? <Badge variant="outline" className={st.cls}>{st.label}</Badge> : "—"}</TableCell>
                      <TableCell>{isProvider ? <ProviderContractCell user={u} onChanged={reloadUsers} /> : "—"}</TableCell>
                      <TableCell>
                        {isProvider ? (
                          <div className="flex items-center gap-1.5">
                            {u.status !== "active" && (
                              <Button size="sm" className="h-8 rounded-lg gap-1 bg-emerald-600 hover:bg-emerald-700" disabled={u.contract?.status !== "uploaded"} title={u.contract?.status !== "uploaded" ? "الاعتماد بعد رفع العقد الموقع" : ""} onClick={() => setStatus(u.id, "active")} data-testid={`approve-${u.id}`}>
                                <Check className="w-3.5 h-3.5" /> اعتماد
                              </Button>
                            )}
                            {u.status !== "rejected" && (
                              <Button size="sm" variant="outline" className="h-8 rounded-lg gap-1 text-destructive border-destructive/30" onClick={() => setStatus(u.id, "rejected")} data-testid={`reject-${u.id}`}>
                                <X className="w-3.5 h-3.5" /> رفض
                              </Button>
                            )}
                          </div>
                        ) : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="requests" className="mt-6">
          <Card className="overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-start">الخدمة</TableHead>
                <TableHead className="text-start">العميل</TableHead>
                <TableHead className="text-start">المدينة</TableHead>
                <TableHead className="text-start">العروض</TableHead>
                <TableHead className="text-start">الحالة</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {requests.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-semibold">{r.service_type}</TableCell>
                    <TableCell>{r.customer?.name}</TableCell>
                    <TableCell>{r.city}</TableCell>
                    <TableCell>{r.quote_count}</TableCell>
                    <TableCell><Badge variant="outline" className={STATUS_COLORS[r.status]}>{STATUS_LABELS[r.status]}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="tows" className="mt-6">
          <Card className="overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-start">المركبة</TableHead>
                <TableHead className="text-start">من</TableHead>
                <TableHead className="text-start">إلى</TableHead>
                <TableHead className="text-start">العروض</TableHead>
                <TableHead className="text-start">الحالة</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {tows.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-semibold">{t.vehicle_type}</TableCell>
                    <TableCell>{t.from_location}</TableCell>
                    <TableCell>{t.to_location}</TableCell>
                    <TableCell>{t.offer_count}</TableCell>
                    <TableCell><Badge variant="outline" className={STATUS_COLORS[t.status]}>{STATUS_LABELS[t.status]}</Badge></TableCell>
                  </TableRow>
                ))}
                {tows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">لا توجد طلبات سحب</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="payments" className="mt-6">
          <AdminPayments />
          <div className="mt-6"><AdminPayouts /></div>
        </TabsContent>

        <TabsContent value="support" className="mt-6">
          <SupportAgents />
        </TabsContent>

        <TabsContent value="ratings" className="mt-6">
          <div className="grid md:grid-cols-2 gap-4">
            {ratings.length === 0 ? <div className="text-muted-foreground py-8 text-center col-span-2">لا توجد تقييمات بعد</div> :
              ratings.map((rt) => (
                <Card key={rt.id} className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">{rt.customer_name}</span>
                    <div className="flex items-center gap-2"><StarDisplay value={rt.overall} size="w-4 h-4" /><span className="font-bold">{rt.overall}</span></div>
                  </div>
                  {rt.comment && <p className="text-sm text-muted-foreground">{rt.comment}</p>}
                </Card>
              ))}
          </div>
        </TabsContent>

        <TabsContent value="cities" className="mt-6">
          <Card className="p-6 max-w-2xl">
            <h3 className="font-bold mb-1 flex items-center gap-2"><MapPin className="w-5 h-5 text-primary" /> إدارة المدن</h3>
            <p className="text-sm text-muted-foreground mb-5">المدن التي تظهر كخيارات عند تسجيل الورش والسطحات والعملاء.</p>
            <form onSubmit={addCity} className="flex items-center gap-2 mb-5">
              <Input value={newCity} onChange={(e) => setNewCity(e.target.value)} placeholder="اسم المدينة الجديدة" data-testid="city-input" />
              <Button type="submit" className="rounded-xl gap-2 shrink-0" data-testid="add-city-btn"><Plus className="w-4 h-4" /> إضافة</Button>
            </form>
            <div className="grid sm:grid-cols-2 gap-2">
              {cities.map((c) => (
                <div key={c.id} className="flex items-center justify-between border border-border rounded-xl px-4 py-2.5" data-testid={`city-${c.id}`}>
                  <span className="flex items-center gap-2 font-medium"><MapPin className="w-4 h-4 text-muted-foreground" /> {c.name}</span>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => delCity(c.id)} data-testid={`del-city-${c.id}`}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              ))}
              {cities.length === 0 && <div className="text-muted-foreground text-sm col-span-2 py-4 text-center">لا توجد مدن. أضف مدينة للبدء.</div>}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </Shell>
  );
}
